import handleError from './error';
import {organizations, quantum, users} from './api';
import {Module} from './data-types';
import quantumAccelerator from './quantum-accelerator';
import activityTracker from './activity-tracker';
import {identifyVisitor} from './analytics-provider';
import {IPublicClientApplication} from '@azure/msal-browser';
import {handleLoginRedirect, handleLogoutRedirect} from './entra-auth/entra-auth';
import {msalInstance} from './entra-auth/entra-auth-provider';
import {safeConsoleLog} from './sanitize';
import {validateHost} from './api-host-whitelist';

const APP_API_BASE_HOST = validateHost(String(process.env.REACT_APP_API_BASE));

// Security: Always use HTTPS for all environments to ensure encrypted transmission
const protocol = 'https://';

let token = sessionStorage.getItem('jwt');
if (!token && URLSearchParams) {
    // Attempt to get it from the query string
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('token');
}

type AuthOrgType = {
    id: number,
    name: string,
};

type PrincipalType = {
    sub: number,
    name: string,
    email: string,
    exp: number,
    accessRoles: Array<string>,
    orgs: Array<AuthOrgType>,
    throttleLimit?: number,
    modules?: Array<Module>
    landing?: string
};

function getPrincipal(): PrincipalType | null {
    if (!token)
        return null;
    try {
        const p = JSON.parse(atob(token.split('.')[1]));
        if (p.exp && p.exp < new Date().getTime() / 1000)
            return null;
        return p;
    } catch (e) {
        console.error(e);
        return null;
    }
}

class InvalidAuthError extends Error {}

function hasRole(role: string): boolean {
    return !!auth.principal && (
        auth.principal.accessRoles.includes(role) ||
        auth.principal.accessRoles.includes('admin')
    );
}

function hasModule(moduleName: string): boolean {
    return !!auth.principal && !!auth.principal.modules && (auth.principal.modules.some(m => m.name === moduleName));
}

function setToken(newToken: string) {
    // SECURITY: Validate token format before storing
    if (!newToken || typeof newToken !== 'string') {
        console.error('[auth] Invalid token type provided');
        return;
    }

    const trimmedToken = newToken.trim();
    if (trimmedToken === '') {
        console.error('[auth] Empty token provided');
        return;
    }

    // Validate JWT structure (3 base64 parts separated by dots)
    const parts = trimmedToken.split('.');
    if (parts.length !== 3) {
        // CWE-117: Do not log untrusted data. Log a clear, safe message.
        console.error('[auth] Invalid JWT structure: JWT does not have 3 parts (header, payload, signature)');
        return;
    }

    // Validate each part is non-empty
    const [header, payload, signature] = parts;
    if (!header || !payload || !signature) {
        console.error('[auth] Invalid JWT - empty header, payload, or signature');
        return;
    }

    // Validate each part contains only valid base64url characters
    const base64UrlPattern = /^[A-Za-z0-9_-]+$/;
    if (!base64UrlPattern.test(header) || !base64UrlPattern.test(payload) || !base64UrlPattern.test(signature)) {
        console.error('[auth] Invalid JWT - contains invalid base64url characters');
        return;
    }

    // Validate header and payload can be decoded as JSON
    try {
        const decodedHeader = JSON.parse(atob(header.replace(/-/g, '+').replace(/_/g, '/')));
        const decodedPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

        // Validate header has required fields
        if (!decodedHeader.alg || !decodedHeader.typ) {
            console.error('[auth] Invalid JWT header - missing alg or typ');
            return;
        }

        // Validate payload has basic required fields (sub or user identifier)
        if (!decodedPayload.sub && !decodedPayload.id && !decodedPayload.email) {
            console.error('[auth] Invalid JWT payload - missing subject/user identifier');
            return;
        }

        // Check if token is expired (if exp claim exists)
        if (decodedPayload.exp) {
            const now = Math.floor(Date.now() / 1000);
            if (decodedPayload.exp < now) {
                console.error('[auth] JWT token is expired');
                return;
            }
        }
    } catch (e) {
        console.error('[auth] Invalid JWT - cannot decode header or payload:', e);
        return;
    }

    sessionStorage.setItem('jwt', trimmedToken);
    token = trimmedToken;
    auth.token = trimmedToken;
    auth.principal = getPrincipal();
}

function setPrevProxyToken(token: string) {
    sessionStorage.setItem('prev-jwt', token);
}

function login(email: string, password: string) {
    quantumAccelerator.clearObjectStore();

    // Veracode: CWE-201 - Insertion of Sensitive Information Into Sent Data
    // This is the primary authentication endpoint - credential transmission is required.
    // Security controls: HTTPS encryption (line 16), host whitelist validation,
    // same-origin policy, first-party service only. Standard secure authentication practice.
    const authUrl = protocol + APP_API_BASE_HOST + '/v2/user/authenticate';

    return fetch(authUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({email, password}), // CWE-201: Intentional, HTTPS-encrypted credential transmission
    })
        .then(response => {
            if (response.status === 400) {
                if (auth.isExpired()) {
                    auth.clearMsalCache();
                }
                handleLoginRedirect(msalInstance);
            } else if (response.status === 401) {
                throw new InvalidAuthError();
            } else if (response.status > 400)
                throw new Error('Error logging in');
            return response.text();
        })
        .then(newToken => setPostLoginTasks(newToken));
}

function loginWithEntraIDToken(idToken: string) {
    quantumAccelerator.clearObjectStore();

    // Veracode: CWE-201 - Azure AD authentication endpoint
    // ID token transmission is required for Entra ID authentication.
    // Secured by HTTPS encryption and host whitelist validation.
    const authUrl = protocol + APP_API_BASE_HOST + '/v2/user/authenticate/entra-id';

    return fetch(authUrl, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + idToken // CWE-201: Intentional, HTTPS-encrypted token transmission
        },
        credentials: 'same-origin',
    })
        .then(response => {
            if (response.status === 401)
                throw new InvalidAuthError();
            else if (response.status >= 400)
                throw new Error('Error logging in');
            return response.text();
        })
        .then(newToken => setPostLoginTasks(newToken));
}

function setPostLoginTasks(newToken: string) {
    setToken(newToken);
    identifyVisitor();
    quantum.wakeUpDatabase()
        .catch(err => safeConsoleLog(err));
    return newToken;
}




//Disabling the refresh token call for now due to some security concerns. This is also disabled on the back-end.
// function refresh() {
//     // Veracode: CWE-201 - Token refresh endpoint
//     // JWT token transmission is required for session refresh.
//     // Secured by HTTPS encryption and host whitelist validation.
//     const refreshUrl = protocol + APP_API_BASE_HOST + '/v2/user/authenticate/refresh';
//
//     fetch(refreshUrl, {
//         method: 'POST',
//         headers: {
//             'Content-Type': 'application/json',
//             'Authorization': 'Bearer ' + auth.token // CWE-201: Intentional, HTTPS-encrypted token transmission
//         },
//         credentials: 'same-origin',
//     })
//         .then(response => response.text())
//         .then(newToken => setToken(newToken))
//         .catch(err => handleError(err, null));
// }

async function proxy(id: number): Promise<string> {
    quantumAccelerator.clearObjectStore();
    const prevToken = auth.token;
    const newToken = await users.proxy.proxy(id);
    setToken(newToken);
    setPrevProxyToken(prevToken!);
    return newToken;
}

async function proxyOrg(id: number): Promise<string> {
    quantumAccelerator.clearObjectStore();
    const prevToken = auth.token;
    const token = await organizations.proxy(id);
    setToken(token);
    setPrevProxyToken(prevToken!);
    return token;
}

function isProxied(): boolean {
    return !!sessionStorage.getItem('prev-jwt');
}

function isExpired(): boolean {
    return !getPrincipal();
}

function unProxy() {
    if (isProxied()) {
        setToken(sessionStorage.getItem('prev-jwt')!);
        sessionStorage.removeItem('prev-jwt');
        window.location.reload();
    }
}

function clearMsalCache() { 
    try {
        // Clear active account
        const activeAccount = msalInstance.getActiveAccount();
        if (activeAccount) {
            msalInstance.setActiveAccount(null);
        }
        
        // Clear all MSAL localStorage entries (tokens, accounts, cache)
        Object.keys(localStorage).forEach(key => {
            // Remove MSAL configuration keys
            if (key.startsWith('msal.')) {
                localStorage.removeItem(key);
            }
            // Remove MSAL token cache entries (they contain login.windows.net)
            else if (key.includes('login.windows.net')) {
                localStorage.removeItem(key);
            }
            // Remove entries with the MSAL client ID
            else if (process.env.REACT_APP_ENTRA_CLIENT_ID && key.includes(process.env.REACT_APP_ENTRA_CLIENT_ID)) {
                localStorage.removeItem(key);
            }
        });
    } catch (err) {
        console.error('Error clearing MSAL cache:', err);
    }
}

function logOut(entraInstance?: IPublicClientApplication) {
    // Clear IndexedDB quantum_cache database
    quantumAccelerator.clearObjectStore();

    // Clear activity tracker from sessionStorage
    activityTracker.deleteRecentActivity();

    // Clear MSAL cache from localStorage
    clearMsalCache();

    // Clear download handoff entries from localStorage
    Object.keys(localStorage).forEach(key => {
        if (key.startsWith('download:')) {
            localStorage.removeItem(key);
        }
    });

    // Clear ALL sessionStorage to ensure no user data persists
    sessionStorage.clear();

    // Reset in-memory token state
    token = null;
    auth.token = null;
    auth.principal = null;

    if (entraInstance) {
        // MSAL logout handles its own redirect
        handleLogoutRedirect(entraInstance);
    } else {
        // Redirect to login page for non-MSAL logout (e.g., JWT expiration)
        window.location.href = '/login';
    }
}

function getLandingPageUrl(): string {
    return auth.principal?.landing ? auth.principal.landing : '/';
}

const auth = {
    principal: getPrincipal(),
    token,
    setToken,
    hasRole,
    hasModule,
    login,
    loginWithEntraIDToken,
    proxy,
    proxyOrg,
    InvalidAuthError,
    isProxied,
    isExpired,
    unProxy,
    logOut,
    clearMsalCache,
    getLandingPageUrl
};

export default auth;